import { cn } from '../../lib/utils'

type ButtonVariant = 'primary' | 'secondary' | 'tertiary' | 'ghost' | 'danger' | 'icon'
type ButtonSize = 'sm' | 'md' | 'lg'

interface ButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: ButtonVariant
  size?: ButtonSize
  isLoading?: boolean
  children: React.ReactNode
}

export function Button({
  variant = 'primary',
  size = 'md',
  isLoading,
  children,
  className,
  disabled,
  ...props
}: ButtonProps) {
  return (
    <button
      className={cn(
        'inline-flex items-center justify-center gap-2 font-semibold rounded-lg transition-all focus:outline-none focus:ring-2 focus:ring-primary-300 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed active:scale-[0.98]',
        {
          'bg-primary-600 text-white hover:bg-primary-700': variant === 'primary',
          'bg-surface-1 text-text-primary border border-border hover:bg-surface-2': variant === 'secondary',
          'bg-transparent text-primary-600 hover:bg-primary-50': variant === 'tertiary',
          'bg-transparent text-text-secondary hover:bg-surface-2 hover:text-text-primary': variant === 'ghost',
          'bg-error-500 text-white hover:bg-error-600': variant === 'danger',
          'w-9 h-9 p-0 bg-transparent text-text-tertiary hover:bg-surface-2 hover:text-text-secondary': variant === 'icon',
          'px-3 py-1.5 text-xs': size === 'sm',
          'px-4 py-2.5 text-sm min-h-[44px]': size === 'md',
          'px-6 py-3 text-base': size === 'lg',
        },
        className
      )}
      disabled={disabled || isLoading}
      {...props}
    >
      {isLoading && (
        <svg className="animate-spin h-4 w-4" viewBox="0 0 24 24">
          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
        </svg>
      )}
      {children}
    </button>
  )
}
